import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { breederProfileDogsResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';

import WorkingWithBreederButton from './WorkingWithBreederButton';
import ParentDogsMobileMenu from './ParentDogsMobileMenu';
import PricingMobileMenu from './PricingMobileMenu';
import LocationMobileMenu from './LocationMobileMenu';
import WorkingWithBreederSectionSkeleton from './WorkingWithBreederSectionSkeleton';
import MeetBreederButton from './MeetBreederButton';

const MOBILE_MENU_OPTIONS = {
  PARENT_DOGS_MENU: 'parent_dogs_menu',
  LOCATION_MENU: 'location_menu',
  PRICING_MENU: 'pricing_menu'
};

export default function WorkingWithBreederSection() {
  const [selectedMobileMenu, setSelectedMobileMenu] = useState<string>();

  const {
    breederFirstName,
    breederProfileId,
    priceBreakdown,
    priceRangeDescription,
    breederTransportOptionsDescription
  } = useSelector(({ server }: any) => server);

  const {
    isFetching: isParentInfoFetching,
    value: {
      data: { dogs: parentDogs }
    }
  } = useResourceQuery(breederProfileDogsResourceDefinition(breederProfileId));

  const parentDogsWithHealthInfo = parentDogs.filter((dog: any) => {
    const { health_testing: healthTesting } = dog;

    const hasHealthTestingDocs =
      healthTesting &&
      healthTesting.documented_health_tests &&
      healthTesting.documented_health_tests.length > 0;

    return hasHealthTestingDocs;
  });

  const hasDogsWithHealthInfo = parentDogsWithHealthInfo.length > 0;
  const hasParentDogs = parentDogs.length > 0;

  if (isParentInfoFetching) {
    return <WorkingWithBreederSectionSkeleton />;
  }

  function handleButtonClick({
    mobileMenuName,
    viewFeature
  }: {
    mobileMenuName: string;
    viewFeature: string;
  }) {
    setSelectedMobileMenu(mobileMenuName);

    sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: 'expand_details_icon_button',
      view: ANALYTICS_VIEWS.BREEDER_PROFILE,
      view_feature: viewFeature,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.OVERVIEW_TAB
    });
  }

  return (
    <div className="WorkingWithBreederSection container-xxl mv8">
      <h4 className="font-24 mb6 lh-title">Working with {breederFirstName}</h4>
      <div className="flex flex-column gap-8">
        {hasParentDogs && (
          <WorkingWithBreederButton
            title={
              hasDogsWithHealthInfo
                ? 'Parent dogs & health testing'
                : 'Parent dogs'
            }
            subtitle={
              hasDogsWithHealthInfo
                ? `${breederFirstName} has ${parentDogs.length} parent ${parentDogs.length === 1 ? 'dog' : 'dogs'} and has uploaded health tests.`
                : `${breederFirstName} has ${parentDogs.length} parent ${parentDogs.length === 1 ? 'dog' : 'dogs'}.`
            }
            onClick={() =>
              handleButtonClick({
                mobileMenuName: MOBILE_MENU_OPTIONS.PARENT_DOGS_MENU,
                viewFeature:
                  ANALYTICS_VIEW_FEATURES.PARENT_DOGS_MODAL_BOTTOM_SHEET
              })
            }
          />
        )}
        <WorkingWithBreederButton
          title="Location & pick-up"
          subtitle={breederTransportOptionsDescription}
          onClick={() =>
            handleButtonClick({
              mobileMenuName: MOBILE_MENU_OPTIONS.LOCATION_MENU,
              viewFeature:
                ANALYTICS_VIEW_FEATURES.LOCATION_PICKUP_MODAL_BOTTOM_SHEET
            })
          }
        />
        <WorkingWithBreederButton
          title="Pricing & what's included"
          subtitle={
            priceBreakdown
              ? `Puppies from ${breederFirstName} are ${priceBreakdown.subtotal} including vaccinations, socialization, and more.`
              : priceRangeDescription
          }
          onClick={() =>
            handleButtonClick({
              mobileMenuName: MOBILE_MENU_OPTIONS.PRICING_MENU,
              viewFeature: ANALYTICS_VIEW_FEATURES.PRICING_MODAL_BOTTOM_SHEET
            })
          }
        />
        <MeetBreederButton />
      </div>
      <ParentDogsMobileMenu
        isOpen={selectedMobileMenu === MOBILE_MENU_OPTIONS.PARENT_DOGS_MENU}
        closeModal={() => setSelectedMobileMenu('')}
        parentDogs={parentDogs}
      />
      <LocationMobileMenu
        isOpen={selectedMobileMenu === MOBILE_MENU_OPTIONS.LOCATION_MENU}
        closeModal={() => setSelectedMobileMenu('')}
      />
      <PricingMobileMenu
        isOpen={selectedMobileMenu === MOBILE_MENU_OPTIONS.PRICING_MENU}
        closeModal={() => setSelectedMobileMenu('')}
      />
    </div>
  );
}
